import type { LegacyBlock } from './pageBlocks';

export const ARTICLE_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .substring(0, 60);
}

function dateSuffix(date: string | undefined): string {
  if (!date) return '';
  const normalized = date.trim().replace(/[.\s/]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  return normalized ? `-${normalized}` : '';
}

/**
 * Build map block.id -> canonical slug.
 * Base slug from title; when multiple articles share the same base slug,
 * a normalized date suffix is appended for disambiguation.
 */
export function buildArticleSlugMap(blocks: LegacyBlock[] | undefined): Map<string, string> {
  const map = new Map<string, string>();
  if (!blocks) return map;
  const baseCount = new Map<string, number>();
  for (const b of blocks) {
    if (b.title) {
      const base = slugify(b.title);
      baseCount.set(base, (baseCount.get(base) || 0) + 1);
    }
  }
  for (const b of blocks) {
    if (!b.title) continue;
    const base = slugify(b.title);
    if (!base) continue;
    map.set(b.id, (baseCount.get(base) || 0) > 1 ? `${base}${dateSuffix(b.date)}` : base);
  }
  return map;
}

/**
 * Find an article block by URL segment — accepts block UUID (backward compat),
 * canonical slug, or legacy raw-date slug (`base-<date>` with un-normalized date).
 */
export function findArticleBySlug(blocks: LegacyBlock[] | undefined, idOrSlug: string): LegacyBlock | undefined {
  if (!blocks) return undefined;
  if (ARTICLE_UUID_RE.test(idOrSlug)) return blocks.find(b => b.id === idOrSlug);
  const slugMap = buildArticleSlugMap(blocks);
  const canonical = blocks.find(b => slugMap.get(b.id) === idOrSlug);
  if (canonical) return canonical;
  return blocks.find(b => {
    if (!b.title) return false;
    const base = slugify(b.title);
    return base === idOrSlug || `${base}-${b.date ?? ''}` === idOrSlug;
  });
}
